import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, CalendarIcon } from '@heroicons/react/24/outline';
import { Link, useLocation } from '@remix-run/react';
import { ShoppingBag, ShoppingCart, Wallet } from 'lucide-react';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Price } from '~/components/products/Price';
import { CurrencyCode } from '~/generated/graphql';
import { CartLoaderData } from '~/routes/api.active-order';
import { CartContents } from './CartContents';
import { API_URL } from '~/constants';
import { useRootLoader } from '~/utils/use-root-loader';

export function CartTray({
  open,
  onClose,
  activeOrder,
  adjustOrderLine,
  removeItem,
}: {
  open: boolean;
  onClose: (closed: boolean) => void;
  activeOrder: CartLoaderData['activeOrder'];
  adjustOrderLine?: (lineId: string, quantity: number) => void;
  removeItem?: (lineId: string) => void;
}) {
  const currencyCode = activeOrder?.currencyCode || CurrencyCode.Usd;
  const location = useLocation();
  const editable = !location.pathname.startsWith('/checkout');
  const { t } = useTranslation();
  const rootData = useRootLoader();

  // Tab state: 'regular' or 'advanced'
  const [activeTab, setActiveTab] = useState<'regular' | 'advanced'>('regular');

  // Determine presence of item types
  const hasRegular = activeOrder?.lines?.some(
    (l) => !l.customFields?.itemType || l.customFields.itemType === 'regular',
  );
  const hasAdvanced = activeOrder?.lines?.some(
    (l) =>
      l.customFields?.itemType === 'recurring' ||
      l.customFields?.itemType === 'future',
  );

  // If we only have one type, force that tab to be active if it isn't already
  // Note: We can't do this during render easily without effects, but better to just handle the display.
  // Actually, we should sync activeTab state.
  if (hasRegular && !hasAdvanced && activeTab !== 'regular') {
    setActiveTab('regular');
  } else if (!hasRegular && hasAdvanced && activeTab !== 'advanced') {
    setActiveTab('advanced');
  }

  // Filter lines based on activeTab (but if only one type exists, show that type)
  // Actually simpler: just rely on activeTab, but if multiple types exist, show tabs.
  // If only one exists, don't show tabs, just the header title? Or just show the valid tab?
  // User asked: "if only regular, only show regular tab". "If only advanced, only show advanced tab".
  // This implies if only one type, maybe hide the tab switcher or just show one option?
  // "show 1 tab" implies the switcher is still there but maybe only 1 button? Or maybe just no switcher?
  // "only show 1 tab" usually means "don't show the switcher if there's no choice".
  // Let's hide the switcher if only one type exists.

  const showTabs = hasRegular && hasAdvanced;

  const visibleLines =
    activeOrder?.lines?.filter((line) => {
      // @ts-ignore
      const itemType = line.customFields?.itemType || 'regular';
      const isAdvanced = itemType === 'recurring' || itemType === 'future';

      if (showTabs) {
        if (activeTab === 'regular') return !isAdvanced;
        return isAdvanced;
      } else {
        // If tabs are hidden, just show what we have
        return true;
      }
    }) || [];

  const handleSsoLogin = async (e: React.MouseEvent) => {
    e.preventDefault();
    try {
      const ssoEndpoint =
        API_URL.replace('/shop-api', '/sso/login-url') + '?_t=' + Date.now();
      const response = await fetch(ssoEndpoint);
      const data = (await response.json()) as { url?: string };
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('SSO Redirect Error:', error);
    }
  };

  const isSignedIn =
    !!activeOrder?.customer || rootData.merchantContext.capability.isSuperAdmin;
  const merchantSelectionRequired =
    rootData.merchantContext.capability.canSwitchMerchant &&
    !rootData.merchantContext.activeMerchant;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 overflow-hidden z-50"
        onClose={onClose}
      >
        <div className="absolute inset-0 overflow-hidden">
          {/* Backdrop */}
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-gray-900 bg-opacity-60 backdrop-blur-sm transition-opacity" />
          </Transition.Child>

          {/* Cart panel */}
          <div className="fixed inset-y-0 right-0 pl-4 sm:pl-6 lg:pl-10 max-w-full flex">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              <div className="w-screen max-w-[85vw] sm:max-w-md lg:max-w-lg">
                <div className="h-full flex flex-col bg-white shadow-lg overflow-hidden rounded-md border border-gray-200/80">
                  {/* Header */}
                  <div className="bg-white border-b border-gray-200 py-3 px-4">
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <span className="text-xs text-gray-500">
                          Shopping for
                        </span>
                        <Dialog.Title className="text-sm font-semibold text-gray-900">
                          Restaurant No.1
                        </Dialog.Title>
                      </div>
                      <button
                        type="button"
                        className="p-1.5 text-gray-500 hover:text-gray-900 rounded-full transition-colors"
                        onClick={() => onClose(false)}
                      >
                        <span className="sr-only">
                          {t('common.closePanel')}
                        </span>
                        <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>

                    {/* Tabs - Only show if both types exist */}
                    {showTabs && (
                      <div className="flex bg-[#FFF0F3] p-1 rounded-full">
                        <button
                          onClick={() => setActiveTab('regular')}
                          className={`flex-1 py-2 text-sm font-semibold rounded-full transition-all ${
                            activeTab === 'regular'
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          Regular
                        </button>
                        <button
                          onClick={() => setActiveTab('advanced')}
                          className={`flex-1 py-2 text-sm font-semibold rounded-full transition-all ${
                            activeTab === 'advanced'
                              ? 'bg-white text-gray-900 shadow-sm'
                              : 'text-gray-600 hover:text-gray-900'
                          }`}
                        >
                          Advanced
                        </button>
                      </div>
                    )}
                    {/* If only one type, maybe show a static header or nothing specific?
                        User said "Cart sẽ chỉ có 1 tab Regular Order". This usually implies just showing the content without the switcher,
                        or showing the single tab selected.
                        "show 1 tab" -> Visual tab?
                        Usually "Tab" implies the UI element.
                        If I hide the switcher, it looks cleaner.
                        Let's hide the switcher. The logic above handles filtering.
                    */}
                  </div>

                  {/* Cart contents */}
                  <div className="flex-1 overflow-y-auto py-3 px-4">
                    {visibleLines.length > 0 ? (
                      <div className="space-y-3">
                        <CartContents
                          orderLines={visibleLines}
                          currencyCode={currencyCode}
                          editable={editable}
                          removeItem={removeItem}
                          adjustOrderLine={adjustOrderLine}
                          onClose={() => onClose(false)}
                          showSavingsHint={true}
                        />
                      </div>
                    ) : (
                      <div className="flex flex-col items-center justify-center h-56 text-center px-4">
                        <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                          <ShoppingCart className="w-7 h-7 text-gray-400" />
                        </div>
                        <h3 className="text-base font-semibold text-gray-900 mb-2">
                          {t('cart.empty')}
                        </h3>
                        <p className="text-sm text-gray-600 mb-4 max-w-xs">
                          Add some products to your cart to get started
                        </p>
                        <button
                          onClick={() => onClose(false)}
                          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-full bg-primary/10 hover:bg-primary/20 text-primary transition-colors duration-300"
                        >
                          Continue Shopping
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  {activeOrder?.totalQuantity && editable && (
                    <div className="border-t border-gray-200 bg-white py-4 px-4 space-y-3">
                      {/* Balance */}
                      <div className="flex items-center gap-2 text-sm text-gray-600">
                        <Wallet className="w-4 h-4" />
                        <span>Balance: 500.00₹</span>
                      </div>

                      <div className="space-y-1.5">
                        <div className="flex justify-between text-sm text-gray-700">
                          <span>Items ({activeOrder.totalQuantity})</span>
                          <Price
                            priceWithTax={activeOrder.subTotalWithTax ?? 0}
                            currencyCode={currencyCode}
                          />
                        </div>
                        <div className="flex items-center justify-between text-sm text-gray-500">
                          <span>Shipping</span>
                          <span className="text-xs">
                            Calculated at checkout
                          </span>
                        </div>
                      </div>

                      <div className="flex justify-between text-base font-bold text-gray-900 pt-2 border-t border-gray-200">
                        <span>{t('common.subtotal')}</span>
                        <span className="text-[#E80245]">
                          <Price
                            priceWithTax={activeOrder.subTotalWithTax ?? 0}
                            currencyCode={currencyCode}
                            variant="primary"
                          />
                        </span>
                      </div>

                      <div className="pt-2">
                        <Link
                          to={
                            isSignedIn && !merchantSelectionRequired
                              ? '/checkout'
                              : '#'
                          }
                          onClick={(e) => {
                            if (!isSignedIn) {
                              handleSsoLogin(e);
                            } else if (merchantSelectionRequired) {
                              e.preventDefault();
                            } else {
                              onClose(false);
                            }
                          }}
                          className="w-full flex items-center justify-between px-5 py-3 rounded-full bg-[#E80245] text-white font-semibold text-sm hover:bg-[#D0023D] transition-colors shadow-md"
                        >
                          <span>
                            {activeTab === 'advanced'
                              ? 'Schedule Order'
                              : `Go to Checkout (${activeOrder.totalQuantity})`}
                          </span>
                          <Price
                            priceWithTax={activeOrder.subTotalWithTax ?? 0}
                            currencyCode={currencyCode}
                          />
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
